import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrganization, useAuth } from '@clerk/clerk-react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteObjectModal from './DeleteObjectModal';
import InviteUserModal from './InviteUserModal';
import SettingsHeader from './SettingsHeader';

function Users() {
  const { organization } = useOrganization();
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { userId, orgRole } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [inviteToRevoke, setInviteToRevoke] = useState(null);
  const [objectTypeToRevoke, setObjectTypeToRevoke] = useState(null);

  const hasInvitationPermission = orgRole === 'admin';
  const location = useLocation();

  // open the invite modal when linked to from the command palette, then strip
  // the param so a refresh doesn't reopen it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('modal') === 'invite-user') {
      if (hasInvitationPermission) setIsInviteUserModalOpen(true);
      // strip the param regardless so a non-admin isn't left with a dead ?modal=
      params.delete('modal');
      const qs = params.toString();
      window.history.replaceState({}, '', qs ? `${location.pathname}?${qs}` : location.pathname);
    }
  }, [location, hasInvitationPermission]);

  // Clerk's paginated list methods default to pageSize: 10 and do not auto-fetch
  // subsequent pages — walk through every page so orgs with >10 members/invites
  // render in full.
  const fetchAllPages = async (fetch) => {
    const pageSize = 100;
    const all = [];
    let page = 1;
    let totalCount = Infinity;
    while (all.length < totalCount) {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch({ initialPage: page, pageSize });
      all.push(...response.data);
      totalCount = response.total_count;
      page += 1;
    }
    return all;
  };

  const getUsers = async () => {
    if (!organization) return;
    setAllRows([]);
    const memberships = await fetchAllPages((args) => organization.getMemberships(args));
    memberships.forEach((membership) => {
      const { firstName, lastName } = membership.publicUserData;
      let name = 'Unknown';
      if (firstName && lastName) {
        name = `${firstName} ${lastName}`;
      } else if (firstName) {
        name = firstName;
      } else if (lastName) {
        name = lastName;
      }
      // Hide trackstar bot from users list
      if (name === 'Trackstar Bot') {
        return;
      }
      const membershipWithFullName = { ...membership, emailAddress: name };
      setAllRows((prev) => [...prev, membershipWithFullName]);
    });
    if (hasInvitationPermission) {
      const invites = await fetchAllPages(
        (args) => organization.getInvitations({ ...args, status: ['pending'] }),
      );
      invites.forEach((invite) => {
        setAllRows((prev) => [...prev, invite]);
      });
    }
  };

  useEffect(() => {
    setLoadingUsers(true);
    getUsers();
    setLoadingUsers(false);
  }, []);

  const handleOpenInviteUserModal = () => {
    setIsInviteUserModalOpen(true);
  };

  const handleCloseInviteUserModal = () => {
    setIsInviteUserModalOpen(false);
  };

  const invitationRevokeFunction = (r) => {
    r.revoke().then(() => {
      getUsers();
      setIsRevokeModalOpen(false);
    });
  };
  const memberRevokeFunction = (r) => {
    r.destroy().then(() => {
      getUsers();
      setIsRevokeModalOpen(false);
    });
  };

  const handleOpenRevokeModal = (row) => {
    setIsRevokeModalOpen(true);
    setInviteToRevoke(row);
    // pending invite
    if (row.status === 'pending') {
      setObjectTypeToRevoke('invitation');
    } else {
      // full member
      setObjectTypeToRevoke('user');
    }
  };
  const revokeFunction = objectTypeToRevoke === 'invitation' ? invitationRevokeFunction : memberRevokeFunction;

  const revokeDisabled = (row) => {
    // can't do if not admin
    if (orgRole !== 'admin') {
      return true;
    }
    // can't revoke own membership
    if (row.publicUserData?.userId === userId) {
      return true;
    }
    return false;
  };

  const handleCloseRevokeModal = () => {
    setIsRevokeModalOpen(false);
  };

  const sendInvite = (email, role) => {
    organization.inviteMember({ emailAddress: email, role }).then(() => {
      getUsers();
    });
  };

  const columns = [{
    field: 'emailAddress',
    headerName: 'User',
    flex: 1,
    renderCell: (params) => (
      <div className="flex items-center w-3/4 justify-between">
        <span>{params.value}</span>
      </div>
    ),
  },
  {
    field: 'role',
    headerName: 'Role',
    flex: 1,
    renderCell: (params) => {
      const words = params.value.split('_');
      const role = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      return (
        <div className="flex items-center w-3/4 justify-between">
          <span>{role}</span>
        </div>
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    renderCell: (params) => {
      let value = '';
      if (params.value) {
        value = params.value;
      } else {
        value = 'joined';
      }
      return (
        <div className="flex items-center w-3/4 justify-between">
          <span>{value.charAt(0).toUpperCase() + value.slice(1)}</span>
        </div>
      );
    },
  },
  {
    field: 'updatedAt',
    headerName: 'Invitation Sent/Accepted',
    flex: 1,
    renderCell: (params) => (
      <span>{new Date(params.value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
    ),
  },
  {
    field: 'revoke',
    width: 20,
    headerName: '',
    disableColumnMenu: true,
    filterable: false,
    resizable: false,
    sortable: false,
    headerClassName: 'column-header-no-border',
    renderCell: (params) => (
      revokeDisabled(params.row) ? null : (
        <IconButton
          size="small"
          onClick={() => handleOpenRevokeModal(params.row)}
          className="text-surface-400 hover:text-error-dark hover:bg-error-light"
          sx={{ padding: '6px' }}
        >
          <DeleteOutlineIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      )
    ),
  }];

  // DataGrid styling
  const dataGridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#FAFAF9',
      borderBottom: '1px solid #E7E5E4',
      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      fontWeight: 500,
      fontSize: '0.8125rem',
      color: '#78716C',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell': {
      fontSize: '0.8125rem',
      fontWeight: 400,
      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      color: '#44403C',
      borderBottom: '1px solid #F5F5F4',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: '#FAFAF9',
    },
    borderRadius: '10px',
    overflow: 'hidden',
  };

  return (
    <div className="flex flex-col gap-5">
      <SettingsHeader
        title="Users"
        subtitle="Manage who can access this dashboard"
        actions={hasInvitationPermission && (
          <button
            type="button"
            onClick={handleOpenInviteUserModal}
            className="btn btn-accent"
          >
            Invite User
          </button>
        )}
      />
      {hasInvitationPermission
        ? (
          <div className="card overflow-hidden">
            <DataGridPro
              rows={allRows}
              columns={columns}
              pageSizeOptions={[]}
              pagination
              autoHeight
              loading={loadingUsers || allRows.length === 0}
              rowHeight={48}
              sortModel={[{ field: 'created_at', sort: 'desc' }]}
              sx={dataGridSx}
            />
          </div>
        )
        : (
          <div className="p-4 bg-surface-50 rounded-lg border border-surface-100 text-center">
            <p className="text-sm text-surface-500">Only admins can manage users</p>
          </div>
        )}
      <DeleteObjectModal
        isOpen={isRevokeModalOpen}
        onClose={handleCloseRevokeModal}
        onDelete={(row) => { revokeFunction(row); }}
        isLoading={false}
        row={inviteToRevoke}
        objectType={objectTypeToRevoke}
      />
      <InviteUserModal
        isOpen={isInviteUserModalOpen}
        onClose={handleCloseInviteUserModal}
        sendInvite={sendInvite}
      />
    </div>
  );
}

export default Users;
